import { Box, Paper, Typography } from "@mui/material";
import { LeadsManagementData } from "../hooks/useLeadsManagementData";
import { formatCurrency } from "../constants";

interface IncomeByBrandChartProps {
  data: LeadsManagementData;
}

export default function IncomeByBrandChart({ data }: IncomeByBrandChartProps) {
  const { aggregations, goToFilteredLeads, getBrandUidByName } = data;

  // income_by_brand is computed server-side over the full filtered result
  // set (see the brand_leads_report/ response spec). Clicking a row opens
  // the Leads tab with that brand in its Brand Name filter.
  const breakdown = [...aggregations.incomeByBrand.byBrand].sort((a, b) => b.amount - a.amount);
  const maxIncome = Math.max(1, ...breakdown.map((row) => row.amount));

  return (
    <Paper variant="outlined" data-tracker="969" sx={{ p: "20px 26.5px", borderRadius: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
        <Typography sx={{ flex: 1, fontSize: 18, fontWeight: 500, color: "#1a1a1a" }}>
          Income by Brand
        </Typography>
        <Typography sx={{ fontSize: 13, color: "grey.500" }}>
          Total:{" "}
          <Typography
            component="span"
            sx={{
              fontFamily: "Roboto",
              fontWeight: 600,
              fontSize: 14,
              lineHeight: "22px",
              letterSpacing: "0.1px",
              verticalAlign: "middle",
              color: "success.main",
              marginLeft: 0.5,
            }}
          >
            {formatCurrency(aggregations.incomeByBrand.total)}
          </Typography>
        </Typography>
      </Box>
      <Typography sx={{ fontSize: 12.5, color: "grey.500", mb: 3 }}>
        Income received from each brand partner for qualified leads.
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: "24px" }}>
        {breakdown.length === 0 ? (
          <Typography sx={{ fontSize: 13.5, color: "grey.500" }}>
            No brand income recorded yet.
          </Typography>
        ) : (
          breakdown.map((row) => {
            // Not clickable if the brand isn't in the brand directory yet.
            const brandUid = getBrandUidByName(row.brandName);
            return (
              <Box
                key={row.brandName}
                onClick={brandUid ? () => goToFilteredLeads({ brand: brandUid }) : undefined}
                title={brandUid ? "View these leads" : undefined}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  cursor: brandUid ? "pointer" : "default",
                }}
              >
                <Typography
                  sx={{
                    width: 150,
                    fontFamily: "Roboto",
                    fontWeight: 400,
                    fontSize: 14,
                    lineHeight: "22px",
                    letterSpacing: "0.17px",
                    color: "text.secondary",
                    textAlign: "right",
                  }}
                >
                  {row.brandName}
                </Typography>
                <Box
                  sx={{
                    flex: 1,
                    bgcolor: "#9C27B014",
                    borderRadius: "2px",
                    height: 28,
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      height: "100%",
                      borderRadius: "2px",
                      bgcolor: "secondary.dark",
                      width: `${Math.round((row.amount / maxIncome) * 100)}%`,
                    }}
                  />
                </Box>
                <Typography
                  sx={{
                    minWidth: 74,
                    fontFamily: "Roboto",
                    fontWeight: 500,
                    fontSize: 14,
                    lineHeight: "22px",
                    letterSpacing: "0.1px",
                    color: "secondary.main",
                    textAlign: "right",
                  }}
                >
                  {formatCurrency(row.amount)}
                </Typography>
              </Box>
            );
          })
        )}
      </Box>
    </Paper>
  );
}
